import type { BillingInterval, PlanId } from '@tandemone/types'
import { PLAN_CATALOG } from '@tandemone/types'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'

import { intervalLabel } from './plan-price'

/* onboarding has no PlanChangeDialog to lean on: nothing is charged yet, so
 * there is no preview to show. the switch still happened in one click with no
 * statement of what changed, which is what people asked for. */
export function PlanSwitchConfirmDialog({
  from,
  to,
  interval,
  onConfirm,
  onCancel
}: {
  from: PlanId
  to: PlanId | null
  interval: BillingInterval
  onConfirm: (planId: PlanId) => void
  onCancel: () => void
}) {
  if (!to) return null
  const fromPlan = PLAN_CATALOG[from]
  const toPlan = PLAN_CATALOG[to]
  const cheaper = toPlan.monthlyPriceCents < fromPlan.monthlyPriceCents
  const free = toPlan.monthlyPriceCents === 0

  return (
    <AlertDialog open onOpenChange={open => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Switch to {toPlan.label}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='flex flex-col gap-2 text-[14px]'>
              <p>
                You currently have {fromPlan.label} selected. This replaces it
                with {toPlan.label}
                {!free && `, billed ${intervalLabel(interval).toLowerCase()}`}.
              </p>
              {free ? (
                <p>
                  {toPlan.label} is free. You will not be billed, and you can
                  pick a paid plan again later.
                </p>
              ) : (
                <p>
                  Nothing is charged now — your plan starts once your profile is
                  approved.
                  {cheaper &&
                    ` ${toPlan.label} includes less than ${fromPlan.label}.`}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep {fromPlan.label}</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(to)}>
            Switch to {toPlan.label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
